import React, { useRef, useState, useEffect } from "react";
import TimeGridView from "./TimeGridView";
import ClassBox, { ClassBoxCompact } from "./ClassBox";
import {
  ScheduleConfig,
  DEFAULT_SCHEDULE_CONFIG,
  ClassData,
  getDayKey,
  getTeacherDayOrder,
  computeHourRange,
} from "../types/schedule";

type ScheduleGridProps = {
  /** 그리드 상단에 표시할 제목 (예: 교사 이름). 없으면 렌더링 생략. */
  title?: string;
  /** 이 그리드에 표시할 수업 목록 */
  classes: ClassData[];
  /** 그리드 렌더링 설정. 생략 시 DEFAULT_SCHEDULE_CONFIG 사용. */
  config?: ScheduleConfig;
  /** "detail": 수강생 목록 박스 안 표시 (기본값) | "compact": 반 이름만 + 호버 팝오버 */
  variant?: "detail" | "compact";
  hoveredKey: string | null;
  setHoveredKey: (key: string | null) => void;
  onClassClick: (classId: number) => void;
};

const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  title,
  classes,
  config: configProp,
  variant = "detail",
  hoveredKey,
  setHoveredKey,
  onClassClick,
}) => {
  const config = configProp ?? DEFAULT_SCHEDULE_CONFIG;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { minHour, maxHour } = computeHourRange(classes, config);
  const containerHeight = (maxHour - minHour + 1) * config.hourHeight;

  // 수업이 있는 요일 인덱스만 추림 (시간 범위 축소와 동일한 원리)
  const activeDayIndices = Array.from(
    { length: config.dayColumns },
    (_, i) => i
  ).filter((dayIndex) => {
    const dayKey = getDayKey(dayIndex);
    return classes.some(
      (cls) =>
        cls[dayKey] &&
        cls[`${dayKey}StartTime`] &&
        cls[`${dayKey}EndTime`]
    );
  });

  // 활성 요일 수 기반으로 config를 덮어씀 (TimeGridView / ClassBox에 전달)
  const effectiveConfig =
    activeDayIndices.length < config.dayColumns
      ? { ...config, dayColumns: activeDayIndices.length }
      : config;

  // 전체 dayIndex → 활성 열 위치 맵 (ClassBox X 좌표 계산에 사용)
  const dayIndexToActivePos = new Map<number, number>();
  activeDayIndices.forEach((globalIdx, pos) => {
    dayIndexToActivePos.set(globalIdx, pos);
  });

  // 요일선: 활성 요일 + 오른쪽 끝 경계선 1개
  const dayLineDataArray = [
    ...activeDayIndices.map((i) => ({ label: config.days[i] ?? "" })),
    { label: "" }, // 오른쪽 끝 경계선
  ];

  // 시간선
  const timeLineDataArray = Array.from(
    { length: maxHour - minHour + 1 },
    (_, i) => minHour + i
  );

  return (
    <div>
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: "100%",
          height: `${containerHeight}px`,
          marginTop: 20, // 요일 라벨 + 선생님 배지 공간
        }}
      >
        {/* 선생님 이름 배지: 첫 번째 요일선 위에 표시 */}
        {title && (
          <div
            style={{
              position: "absolute",
              left: 25,
              top: -25,
              background: "#333",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              borderRadius: 4,
              padding: "2px 7px",
              zIndex: 20,
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </div>
        )}
        <TimeGridView
          minClassHour={minHour}
          maxClassHour={maxHour}
          containerHeight={containerHeight}
          timeLineDataArray={timeLineDataArray}
          dayLineDataArray={dayLineDataArray}
          containerWidth={containerWidth}
          config={effectiveConfig}
        />

        {/* 수업 박스 렌더링 */}
        {classes.map((cls) =>
          Array.from({ length: config.dayColumns }, (_, dayIndex) => {
            const dayKey = getDayKey(dayIndex);
            const isEnabled = cls[dayKey] as boolean;
            const startTime = cls[`${dayKey}StartTime`] as string | null;
            const endTime = cls[`${dayKey}EndTime`] as string | null;

            if (isEnabled && startTime && endTime) {
              const activePos = dayIndexToActivePos.get(dayIndex);
              if (activePos === undefined) return null; // 활성 요일 아님(예외 방어)
              const order = getTeacherDayOrder(cls, dayIndex, classes);
              const boxKey = `${cls.id}-${dayIndex}`;
              const BoxComponent = variant === "compact" ? ClassBoxCompact : ClassBox;
              return (
                <BoxComponent
                  key={boxKey}
                  cls={cls}
                  dayIndex={activePos}
                  startTime={startTime}
                  endTime={endTime}
                  order={order}
                  hoveredKey={hoveredKey}
                  setHoveredKey={setHoveredKey}
                  containerWidth={containerWidth}
                  minClassHour={minHour}
                  config={effectiveConfig}
                />
              );
            }
            return null;
          })
        )}
      </div>
    </div>
  );
};

export default ScheduleGrid;

